import { useEffect, useState, type ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { Button } from './Button'

/**
 * Замена Alert.alert из React Native — в мобильном приложении их 30 штук.
 *
 * Императивный вызов confirm() специально сохранён: он позволяет переносить
 * обработчики один в один, не разворачивая каждый из них в состояние
 * компонента. Диалог рендерится один на всё приложение через <ConfirmHost/>.
 */

export type ConfirmOptions = {
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  destructive?: boolean
}

type PendingConfirm = ConfirmOptions & { resolve: (ok: boolean) => void }

let emit: ((request: PendingConfirm) => void) | null = null

export function confirm(options: ConfirmOptions): Promise<boolean> {
  if (!emit) {
    // ConfirmHost ещё не смонтирован — не блокируем действие молча
    return Promise.resolve(window.confirm(options.title))
  }
  return new Promise<boolean>((resolve) => {
    emit?.({ ...options, resolve })
  })
}

export function ConfirmHost(): ReactNode {
  const [request, setRequest] = useState<PendingConfirm | null>(null)

  useEffect(() => {
    emit = setRequest
    return () => {
      emit = null
    }
  }, [])

  const close = (result: boolean) => {
    request?.resolve(result)
    setRequest(null)
  }

  return (
    <Dialog.Root
      open={!!request}
      onOpenChange={(open) => {
        // Закрытие по Escape или клику вне окна равнозначно отмене
        if (!open) close(false)
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2',
            'rounded-bubble bg-surface p-6 shadow-xl focus:outline-none',
          )}
        >
          <Dialog.Title className="text-lg font-semibold text-gray-900">
            {request?.title}
          </Dialog.Title>

          {request?.description ? (
            <Dialog.Description className="mt-2 text-muted">
              {request.description}
            </Dialog.Description>
          ) : (
            // Radix требует описание либо явного отказа от него
            <Dialog.Description className="sr-only">Подтвердите действие</Dialog.Description>
          )}

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => close(false)}>
              {request?.cancelText ?? 'Отмена'}
            </Button>
            <Button
              variant={request?.destructive ? 'danger' : 'primary'}
              onClick={() => close(true)}
              autoFocus
            >
              {request?.confirmText ?? 'Продолжить'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
